import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown, 
  ChevronUp 
} from 'lucide-react';

interface SidebarProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (value: boolean) => void;
  isSidebarCollapsed?: boolean;
  setIsSidebarCollapsed?: (value: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ 
  isMobileMenuOpen, 
  setIsMobileMenuOpen,
  isSidebarCollapsed = false,
  setIsSidebarCollapsed = () => {}
}) => {
  const [isStrategiesOpen, setIsStrategiesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const strategySubcategories = [
    { id: 'trend-following', name: 'Trend Following', icon: <span className="text-lg">📈</span> },
    { id: 'scalping', name: 'Scalping', icon: <span className="text-lg">⚡</span> },
    { id: 'support-resistance', name: 'Support & Resistance', icon: <span className="text-lg">🎯</span> },
    { id: 'candle-patterns', name: 'Candle Patterns', icon: <span className="text-lg">🕯️</span> },
    { id: 'swing-trading', name: 'Swing Trading', icon: <span className="text-lg">🔄</span> },
    { id: 'chart-patterns', name: 'Chart Patterns', icon: <span className="text-lg">📊</span> },
    { id: 'fvg', name: 'Fair Value Gap (FVG)', icon: <span className="text-lg">💎</span> }
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.startsWith('/bot/')) return 'my-bots';
    if (path === '/dashboard') return 'dashboard';
    return path.slice(1);
  };

  const activeTab = getActiveTab();

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleToggleCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div
      className={`${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 fixed top-16 bottom-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-40 flex flex-col ${
        isSidebarCollapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <div className={`px-4 py-2 ${isSidebarCollapsed ? 'text-center' : 'flex items-center justify-between'} mb-4`}>
            {!isSidebarCollapsed && (
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                MAIN
              </span>
            )}
            <button
              onClick={handleToggleCollapse}
              className={`flex items-center justify-center h-6 w-6 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                isSidebarCollapsed ? 'mx-auto' : ''
              }`}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronLeft className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => handleNavigation('/dashboard')}
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'dashboard' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Dashboard' : undefined}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg ${isSidebarCollapsed ? '' : 'mr-3'}`}>📊</span>
              </span>
              {!isSidebarCollapsed && <span>Dashboard</span>}
            </button>

            <button
              onClick={() => handleNavigation('/my-bots')}
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'my-bots' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'My Bots' : undefined}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg ${isSidebarCollapsed ? '' : 'mr-3'}`}>🤖</span>
              </span>
              {!isSidebarCollapsed && <span>My Bots</span>}
            </button>

            <button
              onClick={() => handleNavigation('/live-trades')}
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'live-trades' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Live Trades' : undefined}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg animate-pulse ${isSidebarCollapsed ? '' : 'mr-3'}`}>🔴</span>
              </span>
              {!isSidebarCollapsed && <span>Live Trades</span>}
            </button>

            <button
              onClick={() => handleNavigation('/active-bots')}
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'active-bots' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Active Bots' : undefined}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg ${isSidebarCollapsed ? '' : 'mr-3'}`}>▶️</span>
              </span>
              {!isSidebarCollapsed && <span>Active Bots</span>}
            </button>

            <button
              onClick={() => handleNavigation('/backtests')}
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'backtests' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Backtests' : undefined}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg ${isSidebarCollapsed ? '' : 'mr-3'}`}>📈</span>
              </span>
              {!isSidebarCollapsed && <span>Backtests</span>}
            </button>

            {/* Strategies dropdown */}
            {!isSidebarCollapsed && (
              <div>
                <button
                  onClick={() => setIsStrategiesOpen(!isStrategiesOpen)}
                  className={`flex items-center justify-between w-full px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg transition-colors ${
                    activeTab.startsWith('strategies') ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-lg mr-3">🎲</span>
                    <span>Strategies</span>
                  </div>
                  {isStrategiesOpen ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>
                {isStrategiesOpen && (
                  <div className="ml-7 mt-2 space-y-1">
                    {strategySubcategories.map((subcategory) => (
                      <button
                        key={subcategory.id}
                        onClick={() => handleNavigation(`/strategies/${subcategory.id}`)}
                        className={`w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-lg transition-colors ${
                          activeTab === `strategies-${subcategory.id}` ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {subcategory.icon}
                        <span className="ml-3">{subcategory.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Collapsed version of Strategies */}
            {isSidebarCollapsed && (
              <>
                <button
                  onClick={() => handleNavigation('/strategies')}
                  className={`flex items-center justify-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                    activeTab === 'strategies' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title="Strategies"
                >
                  <span className="text-lg">🎲</span>
                </button>
                <button
                  onClick={() => handleNavigation('/backtests')}
                  className={`flex flex-col items-center py-1 px-2 ${
                    activeTab === 'backtests' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <span className="text-lg">🧪</span>
                  <span className="text-xs mt-1">Backtests</span>
                </button>
                <button
                  onClick={() => handleNavigation('/live-trades')}
                  className={`flex flex-col items-center py-1 px-2 ${
                    activeTab === 'live-trades' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <span className="text-lg animate-pulse">📊</span>
                  <span className="text-xs mt-1">Live Trades</span>
                </button>
              </>
            )}

            <button
              onClick={() => handleNavigation('/marketplace')}
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'marketplace' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Marketplace' : undefined}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg ${isSidebarCollapsed ? '' : 'mr-3'}`}>🛒</span>
              </span>
              {!isSidebarCollapsed && <span>Marketplace</span>}
            </button>

            <Link
              to="/api-keys"
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'api-keys' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg ${isSidebarCollapsed ? '' : 'mr-3'}`}>🔑</span>
              </span>
              {!isSidebarCollapsed && <span>API Keys</span>}
            </Link>

            {!isSidebarCollapsed && (
              <div className="px-4 py-2 mt-4">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  ACCOUNT
                </span>
              </div>
            )}

            <button
              onClick={() => handleNavigation('/settings')}
              className={`flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg w-full transition-colors ${
                activeTab === 'settings' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              title={isSidebarCollapsed ? 'Settings' : undefined}
            >
              <span className="flex-shrink-0">
                <span className={`text-lg ${isSidebarCollapsed ? '' : 'mr-3'}`}>⚙️</span>
              </span>
              {!isSidebarCollapsed && <span>Settings</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};